import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { getApiUrl } from '../config/api';

const RoleManagement = () => {
  const { hasPermission } = useAuth();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permission_ids: []
  });

  // Fetch data
  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await axios.get(getApiUrl('/roles'));
      setRoles(response.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
      if (error.response?.status === 403) {
        setError('Bạn không có quyền xem danh sách vai trò');
      } else {
        setError('Lỗi tải danh sách vai trò');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await axios.get(getApiUrl('/permissions'));
      setPermissions(response.data);
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingRole) {
        await axios.put(getApiUrl(`/roles/${editingRole.id}`), formData);
        alert('Cập nhật vai trò thành công!');
      } else {
        await axios.post(getApiUrl('/roles'), formData);
        alert('Tạo vai trò thành công!');
      }

      fetchRoles();
      setShowModal(false);
      setEditingRole(null);
      setFormData({ name: '', description: '', permission_ids: [] });
      setError('');
    } catch (error) {
      console.error('Error saving role:', error);
      if (error.response?.data?.detail) {
        setError(error.response.data.detail);
      } else {
        setError('Lỗi lưu vai trò');
      }
    }
  };

  const handleDelete = async (roleId) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa vai trò này?')) return;

    try {
      await axios.delete(getApiUrl(`/roles/${roleId}`));
      fetchRoles();
      alert('Xóa vai trò thành công!');
    } catch (error) {
      console.error('Error deleting role:', error);
      if (error.response?.data?.detail) {
        alert(`Lỗi xóa vai trò: ${error.response.data.detail}`);
      } else {
        alert('Lỗi xóa vai trò');
      }
    }
  };

  const openModal = async (role = null) => {
    setEditingRole(role);
    if (role) {
      try {
        // Fetch full role details with permissions
        const response = await axios.get(getApiUrl(`/roles/${role.id}`));
        const roleData = response.data;
        setFormData({
          name: roleData.name,
          description: roleData.description || '',
          permission_ids: roleData.permissions ? roleData.permissions.map(p => p.id) : []
        });
      } catch (error) {
        console.error('Error fetching role details:', error);
        setFormData({
          name: role.name,
          description: role.description || '',
          permission_ids: []
        });
      }
    } else {
      setFormData({ name: '', description: '', permission_ids: [] });
    }
    setShowModal(true);
  };

  const handlePermissionChange = (permissionId) => {
    setFormData(prev => ({
      ...prev,
      permission_ids: prev.permission_ids.includes(permissionId)
        ? prev.permission_ids.filter(id => id !== permissionId)
        : [...prev.permission_ids, permissionId]
    }));
  };

  // Group permissions by resource
  const groupedPermissions = permissions.reduce((acc, permission) => {
    if (!acc[permission.resource]) {
      acc[permission.resource] = [];
    }
    acc[permission.resource].push(permission);
    return acc;
  }, {});

  // Check permission to access this page
  if (!hasPermission('user', 'read')) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-xl font-semibold text-white mb-2">Không có quyền truy cập</h2>
          <p className="text-ocean-100/60">Bạn không có quyền xem danh sách vai trò.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-cyan-500/20 rounded-full"></div>
          <div className="w-16 h-16 border-4 border-cyan-400 rounded-full animate-spin absolute top-0 left-0 border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Quản lý vai trò</h1>
          <p className="text-ocean-100/60">Quản lý vai trò người dùng và phân quyền</p>
        </div>
        {hasPermission('user', 'write') && (
          <button
            onClick={() => openModal()}
            className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white px-4 py-2.5 rounded-xl flex items-center space-x-2 shadow-lg shadow-cyan-500/25 transition-all"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span>Thêm vai trò</span>
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl mb-4">
          {error}
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {roles.map((role) => (
          <div key={role.id} className="bg-dark-800/50 backdrop-blur-xl rounded-2xl border border-cyan-500/20 p-6 hover:border-cyan-500/40 transition-all">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-xl">
                  <svg className="w-5 h-5 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-white">{role.name}</h3>
                  <p className="text-sm text-cyan-400/70">
                    {role.permission_count || 0} quyền hạn
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => openModal(role)}
                  className="text-cyan-400 hover:text-cyan-300 p-2 hover:bg-cyan-500/10 rounded-lg transition-colors"
                  title="Chỉnh sửa"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                {hasPermission('user', 'write') && (
                  <button
                    onClick={() => handleDelete(role.id)}
                    className="text-red-400 hover:text-red-300 p-2 hover:bg-red-500/10 rounded-lg transition-colors"
                    title="Xóa"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {role.description && (
              <p className="text-ocean-100/60 text-sm mb-4">{role.description}</p>
            )}

            <div className="text-xs text-ocean-100/40 pt-3 border-t border-cyan-500/10">
              Ngày tạo: {new Date(role.created_at).toLocaleDateString('vi-VN')}
            </div>
          </div>
        ))}
      </div>

      {(!roles || roles.length === 0) && (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-ocean-100/30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-white">Không có vai trò nào</h3>
          <p className="mt-1 text-sm text-ocean-100/50">Bắt đầu bằng cách thêm vai trò mới.</p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-dark-900/90 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-dark-800 border border-cyan-500/20 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-6 border-b border-cyan-500/10">
              <h2 className="text-xl font-semibold text-white">
                {editingRole ? 'Chỉnh sửa vai trò' : 'Thêm vai trò mới'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-ocean-100/80 mb-2">
                  Tên vai trò
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-3 bg-dark-900 border border-cyan-500/20 rounded-xl text-white placeholder-ocean-100/40 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all outline-none"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-ocean-100/80 mb-2">
                  Mô tả
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-3 bg-dark-900 border border-cyan-500/20 rounded-xl text-white placeholder-ocean-100/40 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all outline-none"
                  rows="3"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-ocean-100/80 mb-3">
                  Quyền hạn
                </label>
                <div className="space-y-4 max-h-60 overflow-y-auto pr-2">
                  {Object.entries(groupedPermissions).map(([resource, perms]) => (
                    <div key={resource} className="bg-dark-900/50 border border-cyan-500/10 rounded-xl p-4">
                      <h4 className="font-medium text-cyan-400 mb-3 capitalize">
                        {resource}
                      </h4>
                      <div className="space-y-2">
                        {perms.map((permission) => (
                          <label key={permission.id} className="flex items-center cursor-pointer group">
                            <input
                              type="checkbox"
                              checked={formData.permission_ids.includes(permission.id)}
                              onChange={() => handlePermissionChange(permission.id)}
                              className="mr-3 w-4 h-4 rounded border-cyan-500/30 bg-dark-900 text-cyan-500 focus:ring-cyan-400/20"
                            />
                            <span className="text-sm text-ocean-100/80 group-hover:text-white transition-colors">
                              {permission.name}
                              {permission.description && (
                                <span className="text-ocean-100/50"> - {permission.description}</span>
                              )}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2.5 text-ocean-100/70 border border-cyan-500/20 rounded-xl hover:bg-cyan-500/10 hover:text-white transition-all"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl hover:from-cyan-400 hover:to-blue-500 shadow-lg shadow-cyan-500/25 transition-all"
                >
                  {editingRole ? 'Cập nhật' : 'Tạo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleManagement;
